import {
  faLongArrowLeft,
  faLongArrowRight,
} from '@fortawesome/pro-regular-svg-icons';
import React, { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { map } from 'rxjs/operators';
import * as yup from 'yup';

import Form, { SubmitType } from '../../../../components/form';
import Button from '../../../../components/form/elements/button';
import Link from '../../../../components/form/elements/link';
import Select, {
  emptySelectOption,
} from '../../../../components/form/elements/select';
import Watch from '../../../../components/form/elements/watch';
import Classifier from '../../../../enums/classifier';
import ClassifierGroup from '../../../../enums/classifier-group';
import ClassifierValue from '../../../../interfaces/classifierValue';
import Paginate from '../../../../interfaces/paginate';
import { Product } from '../../../../interfaces/product';
import { CROP_FIELDS_AND_WORKS_INDEX } from '../../../../router/routes';
import { useService } from '../../../../services/services';
import { route } from '../../../../services/utils';
import { Unit } from '../../../../enums/unit';

import {
  FormData,
  ProductSelectWarehouses,
  ProductsSubmitData,
} from './create';
import ProductSelect from './product-select';
import { Section } from '../../../../pages/crop/fields-and-works';

interface ProductFormValue {
  product: {
    value: string;
  };
}
const schema = yup.object().shape({
  products: yup.lazy(() => {
    return yup.array().of(
      yup.object().shape({
        product_category: yup.object().required().shape({
          value: yup.string().uuid(),
        }),
        product_type: yup.object().required().shape({
          value: yup.string().uuid(),
        }),
        product: yup.object().required().shape({
          value: yup.string().uuid(),
        }),
        warehouses: yup.array().of(
          yup.object().shape({
            quantity: yup
              .number()
              .positive()
              .decimal(8, 3)
              .required()
              .moreThan(0, 'Field must be a positive number'),

            quantity_unit: yup
              .object()
              .required()
              .shape({
                value: yup.string().oneOf(Object.values(Unit)),
              }),

            warehouse: yup.object().shape({
              value: yup.string().required().uuid(),
            }),
          })
        ),
      })
    );
  }),
});

export default function StepOtherProducts({
  formData,
  onReturn,
  onSubmit,
}: {
  formData: FormData;
  onReturn: () => void;
  onSubmit: SubmitType<ProductsSubmitData>;
}) {
  const { t } = useTranslation();
  const tp = useCallback(
    (key: string) => t('pages.crop.works.'.concat(key)),
    [t]
  );
  const {
    classifierService,
    productService,
    warehouseService,
    remainderService,
  } = useService();
  const { farmId, season, type } = useParams<{
    farmId: string;
    season: string;
    type: string;
  }>();

  const [body] = useState(() => [
    ProductSelect(
      {
        name: 'products',
        title: tp('products.title'),
        components: () => [
          Watch(
            Select('product_category', {
              asyncOptions: (value, loadedOptions, { page }) =>
                classifierService
                  .valuesPaged(
                    ClassifierGroup.WAREHOUSE,
                    Classifier.PRODUCT_CATEGORIES_AND_TYPES,
                    { page, value }
                  )
                  .pipe(
                    map((response: Paginate<ClassifierValue>) => ({
                      hasMore: response.last_page > response.current_page,
                      options: response.data.map((productCategory) => ({
                        label: productCategory.value,
                        value: productCategory.id,
                        productCategory: productCategory,
                      })),
                      additional: { page: response.current_page + 1 },
                    }))
                  ),
              multi: false,
              clearable: true,
              label: tp('product_category'),
            }),
            ({ isTouched = false, parentName, setValue, getValues }) => {
              const productCategory = getValues(
                parentName.concat('.product_category')
              );
              const productType = getValues(parentName.concat('.product_type'));
              const product = getValues(parentName.concat('.product'));
              const productCategoryId = product.product?.category?.id;
              const productTypeParentId = product.product?.type?.parent?.id;

              if (isTouched) {
                const commonCondition =
                  !productCategory ||
                  !productType ||
                  (productType.value &&
                    productCategory.value !==
                      (productCategoryId ??
                        productCategory.productCategory?.id) &&
                    productCategory.value !==
                      (productTypeParentId ??
                        productType.productType?.parent?.id));

                if (commonCondition) {
                  setValue(`${parentName}.product_type`, emptySelectOption);
                  setValue(`${parentName}.product`, emptySelectOption);
                }
              }
            },
            false
          ),
          Watch(
            Select('product_type', {
              asyncOptions: (
                value,
                loadedOptions,
                { page, parentName, getValues }
              ) => {
                const params: any = { page, value };
                const categoryId = getValues(
                  parentName.concat('.product_category')
                )?.value;
                if (categoryId) {
                  params.parent_id = categoryId;
                }
                return classifierService
                  .valuesPaged(
                    ClassifierGroup.WAREHOUSE,
                    Classifier.PRODUCT_TYPES,
                    params
                  )
                  .pipe(
                    map((response) => ({
                      hasMore: response.last_page > response.current_page,
                      options: response.data.map((productType) => ({
                        label: `${productType.value} (${productType.parent.value})`,
                        value: productType.id,
                        productType: productType,
                      })),
                      additional: { page: response.current_page + 1 },
                    }))
                  );
              },
              multi: false,
              clearable: true,
              label: tp('product_type'),
              cacheOn: ['product_category.value'],
            }),
            ({ isTouched, parentName, setValue, getValues }) => {
              const productType = getValues(parentName.concat('.product_type'));
              const productCategory = getValues(
                parentName.concat('.product_category')
              );
              const product = getValues(parentName.concat('.product'));

              if (productType?.productType?.id) {
                if (
                  !productCategory ||
                  productCategory.value !== productType.productType.parent.id
                ) {
                  setValue(parentName.concat('.product_category'), {
                    value: productType?.productType?.parent?.id,
                    label: productType?.productType?.parent?.value,
                    productCategory: productType.productType,
                  });
                }
              }
              if (
                isTouched &&
                (!productType ||
                  !productType.value ||
                  productType.value !== product.product?.type?.id)
              ) {
                setValue(parentName.concat('.product'), emptySelectOption);
              }
            }
          ),
          Watch(
            Select('product', {
              asyncOptions: (
                value,
                loadedOptions,
                { page, parentName, getValues }
              ) => {
                const params: any = { page, value };

                const productCategoryId = getValues(
                  parentName.concat('.product_category')
                )?.value;

                if (productCategoryId) {
                  params.product_categories = productCategoryId;
                }

                const productTypeId = getValues(
                  parentName.concat('.product_type')
                )?.value;

                if (productTypeId) {
                  params.product_types = productTypeId;
                }

                const usedProducts = getValues()
                  .products.map(
                    (product: ProductFormValue) => product.product.value
                  )
                  .filter((value: string) => value !== '');

                if (usedProducts) {
                  params.except = usedProducts;
                }

                return productService.index(farmId, params).pipe(
                  map((response: Paginate<Product>) => {
                    return {
                      hasMore: response.last_page > response.current_page,
                      options: response.data.map((product) => ({
                        label: `${product.name} (${product.type.value})`,
                        value: product.id,
                        product: product,
                      })),
                      additional: { page: response.current_page + 1 },
                    };
                  })
                );
              },
              multi: false,
              clearable: true,
              label: tp('product'),
              cacheOn: ['product_category.value', 'product_type.value'],
            }),
            ({ isTouched, parentName, setValue, getValues }) => {
              const product = getValues(parentName.concat('.product'));

              if (product.product?.category) {
                setValue(parentName.concat('.product_category'), {
                  value: product.product.category.id,
                  label: product.product.category.value,
                  productCategory: product.product.category,
                });
              }

              if (product.product?.type) {
                setValue(parentName.concat('.product_type'), {
                  value: product.product.type.id,
                  label: `${product.product.type.value} (${product.product.category.value})`,
                  productType: product.product.type,
                });
              }

              if (isTouched) {
                clearWarehouses(parentName, setValue, getValues);
              }
            }
          ),
        ],
      },
      ProductSelectWarehouses(
        'products',
        type,
        tp,
        farmId,
        warehouseService,
        remainderService
      ),
      {
        label: tp('product_needed'),
        checked: false,
      }
    ),
  ]);

  const [footer] = useState(() => [
    Button(
      'return',
      onReturn,
      'pages.crop.works.return',
      faLongArrowLeft,
      null,
      true
    ),
    Link(
      route(CROP_FIELDS_AND_WORKS_INDEX, {
        farmId: farmId,
        season: season,
        tab: Section.fields,
      }),
      'cancel'
    ),
    Button('continue', null, 'pages.crop.works.continue', faLongArrowRight),
  ]);

  function clearWarehouses(parentName: string, setValue: any, getValues: any) {
    const warehouses = getValues(parentName.concat('.warehouses'));

    Object.entries(warehouses).forEach(([whKey, warehouse]) => {
      const whPath = parentName.concat('.warehouses.', whKey);
      setValue(whPath.concat('.warehouse'), emptySelectOption);
      setValue(whPath.concat('.remainder_unit'), emptySelectOption);
      setValue(whPath.concat('.quantity_unit'), emptySelectOption);
      setValue(whPath.concat('.remainder'), null);
      setValue(whPath.concat('.quantity'), null);
    });
  }
  return (
    <div>
      <Form
        title={formData.id ? tp('edit') : tp('create')}
        mode="step"
        body={body}
        footer={footer}
        values={formData}
        submit={onSubmit}
        schema={schema}
      />
    </div>
  );
}
